import React, { useState, useEffect, useRef, useCallback } from 'react'
import ChatBot from './ChatBot'
import mechasenseLogo from '../../assets/images/mecha_logo_small.png'
import './chatWidget.css'

const POPUP_DEFAULT = { w: 380, h: 560 }
const POPUP_MAX = { w: 570, h: 740 }
const SIZE_STORAGE_KEY = 'ms-chat-widget-size'

function clampPopupSize(w: number, h: number) {
  return {
    w: Math.min(POPUP_MAX.w, Math.max(POPUP_DEFAULT.w, w)),
    h: Math.min(POPUP_MAX.h, Math.max(POPUP_DEFAULT.h, h)),
  }
}

function readStoredPopupSize(): { w: number; h: number } {
  try {
    const raw = localStorage.getItem(SIZE_STORAGE_KEY)
    if (!raw) return POPUP_DEFAULT
    const p = JSON.parse(raw) as { w?: unknown; h?: unknown }
    if (typeof p.w !== 'number' || typeof p.h !== 'number') return POPUP_DEFAULT
    return clampPopupSize(p.w, p.h)
  } catch {
    return POPUP_DEFAULT
  }
}

type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

function sizeForResizeDelta(
  dir: ResizeDir,
  startW: number,
  startH: number,
  dx: number,
  dy: number
): { w: number; h: number } {
  switch (dir) {
    case 'e':
      return { w: startW + dx, h: startH }
    case 'w':
      return { w: startW - dx, h: startH }
    case 's':
      return { w: startW, h: startH + dy }
    case 'n':
      return { w: startW, h: startH - dy }
    case 'se':
      return { w: startW + dx, h: startH + dy }
    case 'sw':
      return { w: startW - dx, h: startH + dy }
    case 'ne':
      return { w: startW + dx, h: startH - dy }
    case 'nw':
      return { w: startW - dx, h: startH - dy }
    default:
      return { w: startW, h: startH }
  }
}

function ChatIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
        fill="white"
      />
    </svg>
  )
}

interface CloseIconProps {
  size?: number
}

function CloseIcon({ size = 20 }: CloseIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18 6L6 18M6 6L18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function MinimizeIcon({ size = 14 }: CloseIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 17h14v2H5v-2z" fill="currentColor" />
    </svg>
  )
}

function ExpandUpIcon({ size = 18 }: CloseIconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6 15l6-6 6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [hasNewResponse, setHasNewResponse] = useState(false)
  const [popupSize, setPopupSize] = useState(() => readStoredPopupSize())
  const widgetRef = useRef<HTMLDivElement>(null)
  const popupSizeRef = useRef(popupSize)
  const isOpenRef = useRef(isOpen)
  const isMinimizedRef = useRef(isMinimized)
  popupSizeRef.current = popupSize
  isOpenRef.current = isOpen
  isMinimizedRef.current = isMinimized

  const onResizePointerDown = useCallback((e: React.PointerEvent, dir: ResizeDir) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startY = e.clientY
    const startW = popupSizeRef.current.w
    const startH = popupSizeRef.current.h

    const onMove = (ev: Event) => {
      const p = ev as PointerEvent
      const dx = p.clientX - startX
      const dy = p.clientY - startY
      const { w, h } = sizeForResizeDelta(dir, startW, startH, dx, dy)
      setPopupSize(clampPopupSize(w, h))
    }
    const onUp = (ev: Event) => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointercancel', onUp)
      document.body.style.removeProperty('user-select')
      const p = ev as PointerEvent
      const dx = p.clientX - startX
      const dy = p.clientY - startY
      const { w, h } = sizeForResizeDelta(dir, startW, startH, dx, dy)
      const next = clampPopupSize(w, h)
      try {
        localStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(next))
      } catch {
        /* ignore */
      }
    }
    document.body.style.userSelect = 'none'
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
    document.addEventListener('pointercancel', onUp)
  }, [])

  const handleResponseReady = useCallback(() => {
    if (!isOpenRef.current || isMinimizedRef.current) setHasNewResponse(true)
  }, [])

  const restoreFromMinimized = useCallback(() => {
    setIsMinimized(false)
    setHasNewResponse(false)
  }, [])

  const handleNavigate = useCallback(() => {
    setIsMinimized(true)
    setHasNewResponse(false)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false)
        setIsMinimized(false)
        setHasNewResponse(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (isOpen && widgetRef.current && e.target instanceof Node && !widgetRef.current.contains(e.target)) {
        setIsOpen(false)
        setIsMinimized(false)
        setHasNewResponse(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [isOpen])

  const handleLauncherClick = () => {
    if (!isOpen) {
      setIsOpen(true)
      setIsMinimized(false)
      setHasNewResponse(false)
    } else if (isMinimized) {
      restoreFromMinimized()
    } else {
      setIsOpen(false)
      setHasNewResponse(false)
    }
  }

  const showLauncherBadge = hasNewResponse && (!isOpen || isMinimized)
  const launcherPulsing = showLauncherBadge

  const popupStyle: React.CSSProperties =
    isOpen && isMinimized
      ? { width: popupSize.w }
      : { width: popupSize.w, height: popupSize.h }

  return (
    <div className="ms-widget-root" ref={widgetRef}>
      <div
        className={`ms-chat-popup${isOpen ? ' ms-chat-popup--open' : ''}${
          isOpen && isMinimized ? ' ms-chat-popup--minimized' : ''
        }`}
        style={popupStyle}
      >
        <div
          className={`ms-widget-header${isMinimized ? ' ms-widget-header--minimized' : ''}${
            hasNewResponse && isMinimized ? ' ms-widget-header--flash' : ''
          }`}
        >
          <div
            className={`ms-widget-header-brand${isMinimized ? ' ms-widget-header-brand--clickable' : ''}`}
            onClick={isMinimized ? restoreFromMinimized : undefined}
            onKeyDown={
              isMinimized
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      restoreFromMinimized()
                    }
                  }
                : undefined
            }
            role={isMinimized ? 'button' : undefined}
            tabIndex={isMinimized ? 0 : undefined}
            aria-label={isMinimized ? 'Expand chat' : undefined}
          >
            <div className="ms-widget-logo">
              <img src={mechasenseLogo} alt="" width={30} height={30} />
            </div>

            <div>
              <div className="ms-widget-title">Roxy</div>
              <div className="ms-widget-subtitle">The Assistant</div>
            </div>
          </div>
          <div className="ms-widget-header-actions">
            {hasNewResponse && isMinimized && <span className="ms-notification-dot" aria-hidden />}
            <div className="ms-status-dot" title="Online" />
            {isOpen && !isMinimized && (
              <button
                className="ms-widget-icon-btn"
                type="button"
                aria-label="Minimize chat"
                onClick={(e) => {
                  e.stopPropagation()
                  setIsMinimized(true)
                }}
              >
                <MinimizeIcon size={14} />
              </button>
            )}
            {isMinimized && (
              <button
                type="button"
                className="ms-widget-icon-btn"
                aria-label="Expand chat"
                title="Expand chat"
                onClick={(e) => {
                  e.stopPropagation()
                  restoreFromMinimized()
                }}
              >
                <ExpandUpIcon size={16} />
              </button>
            )}
            <button
              className="ms-widget-close-btn"
              onClick={() => {
                setIsOpen(false)
                setIsMinimized(false)
                setHasNewResponse(false)
              }}
              aria-label="Close chat"
              type="button"
            >
              <CloseIcon size={14} />
            </button>
          </div>
        </div>

        <ChatBot onResponseReady={handleResponseReady} onNavigate={handleNavigate} />
        <div className="ms-chat-resize-layer" aria-hidden>
          <div
            className="ms-resize-handle ms-resize-n"
            onPointerDown={(ev) => onResizePointerDown(ev, 'n')}
            title="Resize height"
          />
          <div
            className="ms-resize-handle ms-resize-s"
            onPointerDown={(ev) => onResizePointerDown(ev, 's')}
            title="Resize height"
          />
          <div
            className="ms-resize-handle ms-resize-e"
            onPointerDown={(ev) => onResizePointerDown(ev, 'e')}
            title="Resize width"
          />
          <div
            className="ms-resize-handle ms-resize-w"
            onPointerDown={(ev) => onResizePointerDown(ev, 'w')}
            title="Resize width"
          />
          <div
            className="ms-resize-handle ms-resize-ne"
            onPointerDown={(ev) => onResizePointerDown(ev, 'ne')}
            title="Resize"
          />
          <div
            className="ms-resize-handle ms-resize-nw"
            onPointerDown={(ev) => onResizePointerDown(ev, 'nw')}
            title="Resize"
          />
          <div
            className="ms-resize-handle ms-resize-se"
            onPointerDown={(ev) => onResizePointerDown(ev, 'se')}
            title="Resize"
          />
          <div
            className="ms-resize-handle ms-resize-sw"
            onPointerDown={(ev) => onResizePointerDown(ev, 'sw')}
            title="Resize"
          />
        </div>
      </div>

      <button
        className={`ms-launcher-btn${launcherPulsing ? ' ms-launcher-btn--notify' : ''}`}
        onClick={handleLauncherClick}
        aria-label={isOpen && !isMinimized ? 'Close chat' : 'Open chat'}
        type="button"
      >
        {isOpen && !isMinimized ? <CloseIcon size={18} /> : <ChatIcon />}
        {showLauncherBadge && <span className="ms-launcher-badge" aria-label="New message" />}
      </button>
    </div>
  )
}

export default ChatWidget
